// screens/staff/InstructorHomeScreen.tsx
//
// W6 v1 Session 2 — instructor / titolare landing page.
//
// Visible only to roles that can see pedagogical data: titolare,
// instructor_teoria, instructor_guida. Segreteria lands on /staff/studenti
// (Session 1 default) and never sees this surface — the BE 403s on the
// underlying endpoint, and the side-nav hides the link.
//
// Layout per spec §5.2 — six cards in a 3×2 grid:
//
//   [STUDENTI]         [PRONTI ALL'ESAME]   [A RISCHIO]
//   [POLSO CLASSE]     [LACUNE CONTENUTI]   [NOTE NUOVE]
//
// Italian copy is verbatim where the spec gives it. Voice = formal "Lei".

import { useEffect, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTokens } from '../../state/theme';
import { staffApi, type DashboardHomePayload, type StaffMe } from '../../api/staff';
import UrgentBanner from './UrgentBanner';
import { brandLabel } from './brandLabel';

export default function InstructorHomeScreen(): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardHomePayload | null>(null);
  const [me, setMe]     = useState<StaffMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    // W6 v1.1 Session 3 (polish item 6) — fetch `me` alongside dashboard data
    // so the page heading mirrors the topbar's role-conditional brand label.
    // Both endpoints are cheap (single-row reads), and Promise.all keeps the
    // first paint to one round-trip latency.
    Promise.all([
      staffApi.dashboardHome(),
      staffApi.me(),
    ])
      .then(([d, m]) => {
        if (!alive) return;
        setData(d);
        setMe(m);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setError('Non è stato possibile caricare il pannello. Riprovi tra poco.');
        setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  return (
    <section data-testid="instructor-home-screen">
      <header style={{ marginBottom: 24 }}>
        <h1
          data-testid="instructor-home-heading"
          style={{ fontFamily: tokens.fonts.serif, fontSize: 26, fontWeight: 700, margin: 0 }}
        >
          {brandLabel(me?.role)}
        </h1>
        <p style={{ color: tokens.textMuted, margin: '4px 0 0 0', fontSize: 13 }}>
          Sguardo d'insieme sulla classe. Le statistiche pedagogiche sono visibili
          solo a istruttori e titolare.
        </p>
      </header>

      {/* W6 v1.1 — urgent-event banner. Self-contained: shows nothing when
          there are no unread urgent alerts; reads alert_state via /api/staff/alerts/recent. */}
      <UrgentBanner />

      {error && (
        <div role="alert" data-testid="instructor-home-error" style={{ marginBottom: 16, color: tokens.wrong, fontSize: 13 }}>
          {error}
        </div>
      )}

      <div
        data-testid="instructor-home-grid"
        style={{
          display:             'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap:                 16,
        }}
      >
        <Card
          testid="card-studenti"
          header="STUDENTI"
          loading={loading}
          onClick={() => navigate('/staff/studenti')}
          empty={!loading && !error && !data}
        >
          {data && (
            <>
              <BigNumber n={data.studenti.total} label="iscritti" />
              <Sub>
                {data.studenti.activeLast7d} attivi (7gg)
              </Sub>
            </>
          )}
        </Card>

        <Card
          testid="card-pronti"
          header="PRONTI ALL'ESAME"
          loading={loading}
          empty={!loading && !error && !data}
        >
          {data && (
            <>
              <BigNumber n={data.prontiAllEsame.count} label={data.prontiAllEsame.count === 1 ? 'studente' : 'studenti'} />
              <Sub>(5+ sim. superate)</Sub>
            </>
          )}
        </Card>

        <Card
          testid="card-rischio"
          header="A RISCHIO"
          loading={loading}
          empty={!loading && !error && !data}
        >
          {data && (
            <>
              <BigNumber n={data.aRischio.count} label={data.aRischio.count === 1 ? 'studente' : 'studenti'} />
              <Sub>(inattivi 10gg+)</Sub>
            </>
          )}
        </Card>

        <Card
          testid="card-polso"
          header="POLSO CLASSE"
          loading={loading}
          onClick={() => navigate('/staff/pulse')}
          empty={!loading && !error && !data}
        >
          {data && (data.polsoClasse.weak.length === 0 && data.polsoClasse.strong.length === 0) ? (
            <Sub>Nessun dato disponibile.</Sub>
          ) : data && (
            <div>
              <ArgomentoList label="Debole:" items={data.polsoClasse.weak} tokens={tokens} testid="polso-weak" />
              <ArgomentoList label="Forte:"  items={data.polsoClasse.strong} tokens={tokens} testid="polso-strong" />
            </div>
          )}
        </Card>

        {/*
          W6 v1 Session 3 — Session 2's "In arrivo" placeholder is reverted
          now that /staff/lacune mounts. The card is clickable again and
          carries the spec §5.2 verbatim subtext "→ rispondi ora".
        */}
        <Card
          testid="card-lacune"
          header="LACUNE CONTENUTI"
          loading={loading}
          onClick={() => navigate('/staff/lacune')}
          empty={!loading && !error && !data}
        >
          {data && (
            <>
              <BigNumber n={data.lacuneContenuti.count} label="non coperte" />
              <Sub>→ rispondi ora</Sub>
            </>
          )}
        </Card>

        <Card
          testid="card-note"
          header="NOTE NUOVE"
          loading={loading}
          empty={!loading && !error && !data}
        >
          {data && (
            data.noteNuove.count === 0 ? (
              <Sub>Nessuna nuova osservazione.</Sub>
            ) : (
              <>
                <BigNumber n={data.noteNuove.count} label="osservazioni" />
                <Sub>su studenti (ultimi 7gg)</Sub>
              </>
            )
          )}
        </Card>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card primitives
// ---------------------------------------------------------------------------

function Card({
  testid,
  header,
  loading,
  empty,
  onClick,
  badge,
  children,
}: {
  testid:   string;
  header:   string;
  loading:  boolean;
  empty?:   boolean;
  onClick?: () => void;
  /** Optional pill displayed top-right (e.g. "In arrivo" for not-yet-shipped surfaces). */
  badge?:   string;
  children: React.ReactNode;
}): JSX.Element {
  const tokens = useTokens();
  const interactive = !!onClick && !loading;
  const Tag: keyof JSX.IntrinsicElements = interactive ? 'button' : 'div';
  return (
    <Tag
      data-testid={testid}
      onClick={interactive ? onClick : undefined}
      style={{
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'flex-start',
        textAlign:      'left',
        position:       'relative',
        background:     tokens.surface,
        border:         `1px solid ${tokens.line}`,
        borderRadius:   tokens.radius.lg,
        padding:        '20px 22px',
        minHeight:      140,
        cursor:         interactive ? 'pointer' : 'default',
        color:          tokens.text,
        fontFamily:     tokens.fonts.body,
        fontSize:       14,
      }}
      {...(interactive ? { 'aria-label': `Apri ${header.toLowerCase()}` } : {})}
    >
      {badge && (
        <span
          data-testid={`${testid}-badge`}
          style={{
            position:     'absolute',
            top:          12,
            right:        12,
            fontSize:     11,
            fontWeight:   600,
            letterSpacing: 0.2,
            color:        tokens.textMuted,
            background:   tokens.drawerBg,
            border:       `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.pill,
            padding:      '3px 10px',
          }}
        >
          {badge}
        </span>
      )}
      <h2 style={{
        fontSize:      11,
        fontWeight:    700,
        letterSpacing: 1.2,
        color:         tokens.textMuted,
        margin:        '0 0 12px 0',
        textTransform: 'uppercase',
      }}>
        {header}
      </h2>
      {loading ? (
        <Sub testid={`${testid}-loading`}>Caricamento…</Sub>
      ) : empty ? (
        <Sub>Nessun dato disponibile.</Sub>
      ) : children}
    </Tag>
  );
}

function BigNumber({ n, label }: { n: number; label: string }): JSX.Element {
  const tokens = useTokens();
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
      <span style={{ fontFamily: tokens.fonts.serif, fontSize: 36, fontWeight: 700, lineHeight: 1 }}>
        {n}
      </span>
      <span style={{ color: tokens.textMuted, fontSize: 14 }}>{label}</span>
    </div>
  );
}

function Sub({ children, style, testid }: { children: React.ReactNode; style?: React.CSSProperties; testid?: string }): JSX.Element {
  const tokens = useTokens();
  return (
    <div data-testid={testid} style={{ color: tokens.textMuted, fontSize: 13, ...style }}>
      {children}
    </div>
  );
}

function ArgomentoList({
  label,
  items,
  tokens,
  testid,
}: {
  label:  string;
  items:  Array<{ argomento_id: string; name_it: string; mastery: number }>;
  tokens: ReturnType<typeof useTokens>;
  testid: string;
}): JSX.Element {
  if (items.length === 0) {
    return (
      <div style={{ marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>{' '}
        <span style={{ color: tokens.textMuted, fontSize: 13 }}>—</span>
      </div>
    );
  }
  // W6 v1.1 Session 3 (polish item 5) — argomenti `name_it` values contain
  // commas and semicolons (e.g. "Responsabilità civile, penale, amministrativa;
  // R.C.A.; …"), so a flat comma-join produced ambiguous strings like
  // "Debole: A; B, C; D, E". Render as a bullet list so each entry is
  // unambiguous and the card stays scannable. Keeps the same vertical
  // rhythm as the other dashboard-home cards.
  return (
    <div data-testid={testid} style={{ marginBottom: 8 }}>
      <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>
      <ul
        style={{
          margin:     '4px 0 0 0',
          padding:    0,
          listStyle:  'none',
          fontSize:   13,
          color:      tokens.text,
        }}
      >
        {items.map(i => (
          <li
            key={i.argomento_id}
            style={{
              padding:     '2px 0 2px 14px',
              position:    'relative',
              lineHeight:  1.4,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                left:     0,
                top:      2,
                color:    tokens.textMuted,
              }}
            >
              •
            </span>
            {i.name_it}
          </li>
        ))}
      </ul>
    </div>
  );
}
